import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

interface Props {
  accounts: Array<{ id: string; name: string | null }>;
  value: string[];
  onChange: (ids: string[]) => void;
  multiple?: boolean;
  label?: string;
}

export function AccountSelect({ accounts, value, onChange, multiple = true, label = 'Соц акаунти' }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const panelId = useId();
  useEffect(() => {
    if (!accounts.length) return;
    const available = value.filter((id) => accounts.some((account) => account.id === id));
    if (available.length !== value.length) onChange(available);
  }, [accounts, value, onChange]);
  const selected = accounts.filter((account) => value.includes(account.id));
  const visible = useMemo(() => accounts.filter((account) => `${account.name ?? ''} ${account.id}`.toLowerCase().includes(query.trim().toLowerCase())), [accounts, query]);
  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const dismiss = (event: MouseEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', dismiss);
    return () => document.removeEventListener('mousedown', dismiss);
  }, [open]);
  const summary = !value.length ? (multiple ? 'Усі акаунти' : 'Оберіть акаунт') : selected.length === 1 ? selected[0].name ?? selected[0].id : `Обрано: ${value.length}`;
  return <div className={`account-select${open ? ' account-select--open' : ''}`} ref={root} onKeyDown={(event) => { if (event.key === 'Escape') { setOpen(false); root.current?.querySelector('button')?.focus(); } }} onBlur={(event) => {
    // Pointer clicks can temporarily blur to body (relatedTarget=null).
    // Outside pointer clicks are handled by mousedown; only close here when
    // keyboard focus moves to a known element outside the selector.
    if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
  }}>
    <span className="text-muted text-sm">{label}</span>
    <button type="button" className="account-select__trigger input" aria-expanded={open} aria-controls={panelId} onClick={() => { setOpen(!open); setQuery(''); }}>
      <span>{summary}</span><span aria-hidden="true">⌄</span>
    </button>
    {open && <div id={panelId} className="account-select__panel" role="group" aria-label={label}>
      <input ref={input} className="input" placeholder="Пошук за назвою або ID" aria-label="Пошук акаунта" value={query} onChange={(event) => setQuery(event.target.value)} />
      {multiple && <button type="button" className="btn btn--ghost btn--sm" onClick={() => onChange([])}>Усі акаунти · скинути вибір</button>}
      <div className="account-select__options">
        {visible.map((account) => <button type="button" className="account-select__option" key={account.id} aria-pressed={value.includes(account.id)} onClick={() => {
            onChange(multiple ? value.includes(account.id) ? value.filter((id) => id !== account.id) : [...value, account.id] : [account.id]);
            if (!multiple) setOpen(false);
          }}>
          <span className={`account-select__check${value.includes(account.id) ? ' account-select__check--selected' : ''}`} aria-hidden="true">{value.includes(account.id) ? '✓' : ''}</span>
          <span><strong>{account.name ?? account.id}</strong><small>{account.id}</small></span>
        </button>)}
        {!visible.length && <p className="text-sm text-muted">Акаунтів не знайдено</p>}
      </div>
    </div>}
  </div>;
}
